import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Zap, Users, Building2, Mail, Crown, Shield, AlertTriangle, Video, Settings, BookOpen, GraduationCap } from 'lucide-react';

// ============================================================
// FREE TRIAL CARD
// ============================================================
function FreeCard() {
  return (
    <div className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(30,35,45,1) 0%, rgba(25,30,38,1) 100%)',
        border: '2px solid rgba(77,214,255,0.25)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.30)',
      }}>

      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(77,214,255,0.15)', border: '1px solid rgba(77,214,255,0.30)' }}>
            <Zap className="w-5 h-5" style={{ color: '#4dd6ff' }} />
          </div>
          <div>
            <h3 className="text-2xl font-black" style={{ fontFamily: 'Inter, sans-serif', color: '#ffffff' }}>Free</h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full inline-block"
              style={{ background: 'rgba(77,214,255,0.15)', color: '#4dd6ff', border: '1px solid rgba(77,214,255,0.25)' }}>
              Para probar el método
            </span>
          </div>
        </div>

        {/* Trial price */}
        <div className="flex items-end gap-1 mb-2">
          <span className="text-4xl font-black" style={{ fontFamily: 'Inter, sans-serif', color: '#4dd6ff' }}>Gratis</span>
          <span className="text-sm pb-1.5" style={{ color: 'rgba(255,255,255,0.50)' }}>· 15 días</span>
        </div>

        {/* Trial warning box */}
        <div className="p-3 rounded-xl mb-4"
          style={{ background: 'rgba(77,214,255,0.08)', border: '1px solid rgba(77,214,255,0.20)' }}>
          <p className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.90)' }}>
            ⚡ Tras los 15 días pasa a <span style={{ color: '#4dd6ff', fontWeight: 700 }}>Pro 9,99€/mes</span> automáticamente si no cancelas.
          </p>
        </div>
      </div>

      {/* Features */}
      <div className="px-6 pb-2">
        <p className="text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.60)' }}>Límites del plan gratuito:</p>
        <div className="space-y-1.5 mb-4">
          {['1 equipo', '2 microciclos', '3 sesiones', '1 sesión elite', '1 partido', '1 convocatoria', 'Zona padres'].map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4dd6ff' }} />
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.80)' }}>{f}</span>
            </div>
          ))}
        </div>

        <div className="space-y-1.5 pt-3 mb-4" style={{ borderTop: '1px dashed rgba(255,255,255,0.15)' }}>
          <p className="text-xs font-bold mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>No incluye:</p>
          {['Biblioteca de vídeo y clips', 'Convocatorias profesionales', 'Export PDF sin marca de agua'].map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <X className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#ff4d6d' }} />
              <span className="text-sm line-through" style={{ color: 'rgba(255,255,255,0.35)' }}>{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 pb-6">
        <Link to="/login?plan=free"
          className="block w-full py-3 rounded-xl font-bold text-sm text-center transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.15)' }}>
          Empezar prueba gratis
        </Link>
        <p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
          Cancelación en un clic. Sin permanencia.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// PRO CARD (la estrella)
// ============================================================
function ProCard() {
  return (
    <div className="relative rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
      style={{
        background: 'linear-gradient(135deg, rgba(10,35,20,1) 0%, rgba(8,30,18,1) 100%)',
        border: '2px solid rgba(16,185,129,0.70)',
        boxShadow: '0 0 0 1px rgba(16,185,129,0.20), 0 20px 60px rgba(16,185,129,0.35)',
      }}>

      {/* Top glow line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#10B981] to-transparent" />

      {/* Badge */}
      <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-black flex items-center gap-1"
        style={{ background: '#10B981', color: '#000', fontFamily: 'Inter, sans-serif', boxShadow: '0 4px 12px rgba(16,185,129,0.40)' }}>
        <Crown className="w-3 h-3" /> MÁS POPULAR
      </div>

      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(16,185,129,0.20)', border: '1px solid rgba(16,185,129,0.40)' }}>
            <Shield className="w-5 h-5" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h3 className="text-2xl font-black" style={{ fontFamily: 'Inter, sans-serif', color: '#ffffff' }}>Pro</h3>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full inline-block"
              style={{ background: 'rgba(16,185,129,0.20)', color: '#10B981', border: '1px solid rgba(16,185,129,0.35)' }}>
              Entrenador solo
            </span>
          </div>
        </div>

        <div className="flex items-end gap-1 mb-1">
          <span className="text-5xl font-black" style={{ fontFamily: 'Inter, sans-serif', color: '#10B981', filter: 'brightness(1.1)' }}>9,99€</span>
          <span className="text-sm pb-2" style={{ color: 'rgba(255,255,255,0.55)' }}>/mes</span>
        </div>
        <p className="text-xs font-semibold mb-3" style={{ color: '#10B981' }}>✨ 3 días gratis</p>

        <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.85)' }}>
          Para trabajar con método cada semana, sin límites molestos.
        </p>
      </div>

      {/* Features */}
      <div className="px-6 pb-4">
        <div className="space-y-2 mb-4">
          {[
            '1 usuario',
            'Equipos ilimitados',
            'Microciclos ilimitados',
            'Sesiones y tareas ilimitadas',
            'Pizarra ilimitada',
            'Partidos ilimitados',
            'Biblioteca de vídeo y clips',
            'Export PDF sin marca de agua',
            'Comunicación con padres',
            'Historial completo',
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981', filter: 'brightness(1.3)' }} />
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.95)' }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Pink warning */}
        <div className="p-3 rounded-xl mb-4 flex items-start gap-2"
          style={{ background: 'rgba(255,77,109,0.10)', border: '1px solid rgba(255,77,109,0.25)' }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#ff4d6d' }} />
          <p className="text-xs" style={{ color: 'rgba(255,150,150,0.95)' }}>
            El análisis avanzado de vídeo, coordinador multi-equipo y multiusuario están en el plan de <span style={{ fontWeight: 700 }}>Clubes y Academias</span>.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 pb-6">
        <Link to="/login?plan=pro"
          className="block w-full py-3.5 rounded-xl font-black text-sm text-center transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: '#10B981', color: '#000', boxShadow: '0 6px 20px rgba(16,185,129,0.45)', fontFamily: 'Inter, sans-serif' }}>
          Probar Pro
        </Link>
        <p className="text-center text-xs mt-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
          Perfecto si trabajas solo.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// CLUB / ACADEMIA BAND (bottom)
// ============================================================
function ClubBand() {
  const chips = [
    { icon: Video, label: 'Vídeo con clips por jugador' },
    { icon: Users, label: 'Multiusuario con roles' },
    { icon: Building2, label: 'Coordinador multi-equipo' },
    { icon: Settings, label: 'Permisos por rol' },
    { icon: BookOpen, label: 'Biblioteca del club' },
    { icon: GraduationCap, label: 'Zona de aprendizaje' },
  ];

  return (
    <div className="max-w-4xl mx-auto mt-8 rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(167,139,250,0.10) 0%, rgba(77,214,255,0.06) 100%)',
        border: '1px solid rgba(167,139,250,0.25)',
        boxShadow: '0 8px 32px rgba(167,139,250,0.10)',
      }}>
      <div className="p-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          {/* Left */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6" style={{ color: '#a78bfa' }} />
              <h3 className="text-2xl font-black" style={{ fontFamily: 'Inter, sans-serif', color: '#ffffff' }}>
                Clubes & Academias
              </h3>
            </div>
            <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.65)', lineHeight: '1.6' }}>
              Varios equipos, varios entrenadores, control total. Precio a medida.
            </p>

            {/* Chips */}
            <div className="flex flex-wrap gap-2">
              {chips.map(({ icon: Icon, label }, i) => (
                <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)', color: '#c4b5fd' }}>
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="flex-shrink-0 text-center">
            <a href="mailto:admin@micoach.tech"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-black text-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
              style={{
                background: 'linear-gradient(135deg, #ffd34d, #ffb800)',
                color: '#000',
                boxShadow: '0 6px 20px rgba(255,211,77,0.35)',
                fontFamily: 'Inter, sans-serif',
              }}>
              <Mail className="w-4 h-4" />
              Habla con nosotros
            </a>
            <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Respuesta en menos de 24h
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN PRICING SECTION
// ============================================================
export default function PricingSection() {
  return (
    <section className="py-20 px-4" id="precios" data-testid="pricing-section">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="section-label mb-2" style={{ color: 'var(--primary)', filter: 'brightness(1.2)' }}>PRECIOS</p>
          <h2 className="text-display text-5xl mb-4" style={{ color: '#ffffff' }}>
            Elige tu plan
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.65)' }}>
            Empieza gratis 15 días. Sin permanencia. Cancela cuando quieras.
          </p>
        </div>

        {/* Cards — 2 columnas centradas */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-4">
          <FreeCard />
          <ProCard />
        </div>

        {/* Club band */}
        <ClubBand />
      </div>
    </section>
  );
}
